import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/components/AuthProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Download, Sparkles, FileText, Wand2, History, ArrowRight, ImageIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + "/api";

export default function FlyersPage() {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState(null);
  const [fields, setFields] = useState({});
  const [aiBrief, setAiBrief] = useState("");
  const [aiFilling, setAiFilling] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [brandKit, setBrandKit] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/flyers/templates`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setTemplates(r.data.templates))
      .catch(() => toast.error("Couldn't load templates"));
    axios.get(`${API}/flyers/history`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setHistory(r.data.flyers))
      .catch(() => {});
    axios.get(`${API}/brand-kit`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setBrandKit(r.data))
      .catch(() => {});
  }, [token]);

  const pickTemplate = (t) => {
    setActive(t);
    const seed = {};
    t.fields.forEach((f) => { seed[f.key] = ""; });
    if (t.id === "restaurant-menu") {
      seed.sections = "## Starters\nBurrata | with sourdough & olive oil | £9\nCarpaccio | thinly sliced beef, parmesan, rocket | £12\n\n## Mains\nRibeye Steak | 250g with chimichurri | £28\nMushroom Risotto | wild mushrooms, truffle oil | £18\n\n## Desserts\nTiramisu | classic, made in-house | £7\nAffogato | vanilla gelato, espresso | £6";
    }
    setFields(seed);
  };

  const doAiFill = async () => {
    if (!active || !aiBrief.trim()) { toast.error("Tell me what the flyer is about first"); return; }
    setAiFilling(true);
    try {
      const r = await axios.post(`${API}/flyers/ai-fill`, { template_id: active.id, brief: aiBrief }, { headers: { Authorization: `Bearer ${token}` } });
      setFields((prev) => ({ ...prev, ...r.data.fields }));
      toast.success("AI filled the fields — review and edit before generating");
    } catch (e) {
      toast.error("AI-fill failed");
    }
    setAiFilling(false);
  };

  const doGenerate = async () => {
    if (!active) return;
    setGenerating(true);
    try {
      // sections is now a plain textarea — backend's _parse_sections handles it
      const payloadFields = { ...fields };
      const res = await axios.post(`${API}/flyers/generate`, { template_id: active.id, fields: payloadFields }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      // Force-download the PDF
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${active.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded — print-ready");
      // Refresh history
      const h = await axios.get(`${API}/flyers/history`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(h.data.flyers);
    } catch (e) {
      toast.error("PDF generation failed");
    }
    setGenerating(false);
  };

  const brandComplete = brandKit && (brandKit.business_name || brandKit.logo_url);

  return (
    <div className="space-y-6" data-testid="flyers-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold mb-1 flex items-center gap-2">
            <FileText className="w-7 h-7 text-violet-500" /> Flyers & Menus
            <Badge className="bg-teal-500/15 text-teal-400 border border-teal-500/30 ml-2">New</Badge>
          </h1>
          <p className="text-muted-foreground text-sm">Print-ready PDFs in 10 seconds. Brand Kit colours + logo auto-applied. AI writes the copy from one sentence.</p>
        </div>
        {!brandComplete && (
          <Link to="/dashboard/settings" className="text-xs px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20" data-testid="brand-kit-nudge">
            ⚠ Fill in your Brand Kit first →
          </Link>
        )}
      </div>

      {/* Template Picker */}
      {!active && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="template-picker">
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => pickTemplate(t)}
              className="text-left p-5 rounded-xl bg-white/5 border border-white/10 hover:border-violet-500/40 hover:bg-white/10 transition-all"
              data-testid={`template-${t.id}`}
            >
              <ImageIcon className="w-7 h-7 text-violet-400 mb-3" />
              <h3 className="font-semibold mb-1">{t.label}</h3>
              <p className="text-xs text-muted-foreground">{t.description}</p>
            </button>
          ))}
        </div>
      )}

      {/* Builder */}
      {active && (
        <Card className="glass" data-testid="flyer-builder">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center gap-2">{active.label}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setActive(null); setFields({}); }} data-testid="change-template-btn">← Change template</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* AI-fill block */}
            <div className="p-4 rounded-lg bg-gradient-to-br from-violet-500/5 to-purple-500/5 border border-violet-500/20">
              <Label className="flex items-center gap-1.5 text-sm font-semibold"><Wand2 className="w-4 h-4 text-violet-500" /> Skip the typing — describe it in one sentence</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={aiBrief}
                  onChange={(e) => setAiBrief(e.target.value)}
                  placeholder="e.g. Sunday brunch pop-up celebrating our 1-year anniversary, casual, live music"
                  className="bg-white/5 border-white/10"
                  data-testid="ai-brief-input"
                />
                <Button onClick={doAiFill} disabled={aiFilling || !aiBrief.trim()} className="bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="ai-fill-btn">
                  {aiFilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-1.5" />AI fill</>}
                </Button>
              </div>
            </div>

            {/* Manual field editor — AI-filled values are editable */}
            <div className="space-y-3">
              {active.fields.map((f) => (
                <div key={f.key} data-testid={`field-${f.key}`}>
                  <Label className="text-xs">{f.label}</Label>
                  {f.type === "textarea-large" ? (
                    <Textarea
                      value={fields[f.key] || ""}
                      onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      className="bg-white/5 border-white/10 min-h-[180px] font-mono text-xs"
                      data-testid={`field-input-${f.key}`}
                    />
                  ) : f.type === "textarea" ? (
                    <Textarea
                      value={fields[f.key] || ""}
                      onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      className="bg-white/5 border-white/10 min-h-[80px]"
                      data-testid={`field-input-${f.key}`}
                    />
                  ) : (
                    <Input
                      value={fields[f.key] || ""}
                      onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      className="bg-white/5 border-white/10"
                      data-testid={`field-input-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>

            <Button onClick={doGenerate} disabled={generating} size="lg" className="w-full bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="generate-pdf-btn">
              {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Rendering PDF…</> : <><Download className="w-4 h-4 mr-2" />Download print-ready PDF</>}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* History */}
      {history.length > 0 && (
        <Card className="glass" data-testid="flyer-history">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><History className="w-5 h-5" /> Recently generated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.slice(0, 10).map((h) => (
                <div key={h.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5 text-sm" data-testid={`history-row-${h.id}`}>
                  <div>
                    <p className="font-medium">{h.template_id}</p>
                    <p className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleString()} · {(h.size_bytes / 1024).toFixed(1)} KB</p>
                  </div>
                  <button onClick={() => { const t = templates.find((x) => x.id === h.template_id); if (t) { setActive(t); setFields(h.fields || {}); } }} className="text-xs text-violet-400 hover:text-violet-300 underline flex items-center gap-1">
                    Re-edit <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
